/* eslint-disable no-extra-parens */
/* eslint-disable react/display-name */
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { reduxConnect } from '../../common';
import { gpcSignalStatusAction, statePrivacyUpdate } from '../../common/actions/statePrivacyActions';
import { UserProvider } from '../../common/providers/userProvider';
import { translate } from '../../common/translation/translate';
import { getCcpaCookie, getStatePrivacyPolicyCookie } from '../../common/utilities/ccpaHelper';
import { deleteCookie, setJsonCookie } from '../../common/utilities/cookie';
import type { IStoreState, StatePrivacyPolicyCookieType } from '../common/interfaces/types';
import { WbStatePrivacyCookieKey } from '../common/interfaces/types';
import { routeHelper, weatherBugAdsForAdvertisersRoute } from '../common/utilities/routeHelper';
import { StyledLink } from './commonStyle';
import { StatePrivacyLinkView } from './statePrivacyLinkView';
import { UsStatePrivacyPolicyLink } from './usStatePrivacyPolicyLink';

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { statePrivacy } = storeState || {};
    const { appInstanceId } = storeState?.appState?.appState || {};
    const { complianceRequirements } = storeState?.legal || {};
    const { dataPrivacyEnabled, sensitiveDataRestrictionEnabled } = complianceRequirements || {};

    return {
        appInstanceId,
        dataPrivacyEnabled,
        sensitiveDataRestrictionEnabled,
        gpcCheckComplete: statePrivacy?.gpcCheckComplete,
        gpcEnabled: statePrivacy?.gpcEnabled 
    };
};

const mapDispatchToProps = {
    statePrivacyUpdate
};

const year = new Date().getFullYear();

interface CopyrightFooterProps {
    appInstanceId: string;
    dataPrivacyEnabled: boolean;
    sensitiveDataRestrictionEnabled: boolean;
    gpcCheckComplete: boolean;
    gpcEnabled: boolean;
    statePrivacyUpdate: () => void;
}

export const CopyrightFooter = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(memo(({  
    appInstanceId,
    dataPrivacyEnabled,
    sensitiveDataRestrictionEnabled,
    gpcCheckComplete,
    gpcEnabled,
    statePrivacyUpdate
}: CopyrightFooterProps) => {
    const [ userOptedOut, setUserOptedOut ] = useState<StatePrivacyPolicyCookieType | null>();
    const [ statePrivacyOptOutApiIsLoading, setStatePrivacyOptOutApiIsLoading ] = useState(false);
    const [ statePrivacyOptOutErrorOccurred, setStatePrivacyOptOutErrorOccurred] = useState(false);
    const [ statePrivacyOptOutSuccessOccurred, setStatePrivacyOptOutSuccessOccurred] = useState(false);

    const [ limitUseOfPIOptOutApiIsLoading, setLimitUseOfPIOptOutApiIsLoading ] = useState(false);
    const [ limitUseOfPIOptOutErrorOccurred, setLimitUseOfPIOptOutErrorOccurred] = useState(false);
    const [ limitUseOfPIOptOutSuccessOccurred, setLimitUseOfPIOptOutSuccessOccurred] = useState(false);
    
    const isInitialLoadComplete = useRef<boolean>(false);

    useEffect(()=> {
        if (!isInitialLoadComplete.current && gpcCheckComplete) {
            isInitialLoadComplete.current = true;

            // This cookie key would only be available after this feature is deployed
            const statePrivacyCookie = getStatePrivacyPolicyCookie(WbStatePrivacyCookieKey);
            
            // TODO:
            // Handle situation where ccpa cookies exists for users who had previously disabled/clicked "Do Not Sell My Data"
            const oldCookiesFromCCPA = getCcpaCookie();

            if (statePrivacyCookie) {
                setUserOptedOut(statePrivacyCookie);
            }

            if (oldCookiesFromCCPA?.doNotSell) { // converts existing ccpa privacy cookie to new state privacy 
                setUserOptedOut(oldCookiesFromCCPA);
            }
        }
    }, [gpcCheckComplete]);

    useEffect(() => {
        if (gpcEnabled) {
            setUserOptedOut((prevState) => prevState ? { ...prevState, doNotSell: true } : { doNotSell: true });
        }
    }, [gpcEnabled]);

    const handleStatePrivacyLinkClick = useCallback(async () => {
        try {
            const newStatePrivacyOptOutValue = !userOptedOut?.doNotSell;
            let updatedOptOutState: StatePrivacyPolicyCookieType  = userOptedOut ? { ...userOptedOut } : {} as StatePrivacyPolicyCookieType;
            setStatePrivacyOptOutApiIsLoading(true);
            setStatePrivacyOptOutErrorOccurred(false);
            setStatePrivacyOptOutSuccessOccurred(false);
            await UserProvider.updateStatePrivacy(appInstanceId, newStatePrivacyOptOutValue);
            if (newStatePrivacyOptOutValue) {
                updatedOptOutState = {
                    ...updatedOptOutState,
                    doNotSell: true
                };
                setJsonCookie(WbStatePrivacyCookieKey, { ...updatedOptOutState }, 10950);
            } else {
                updatedOptOutState = {
                    ...updatedOptOutState,
                    doNotSell: false
                };

                if (userOptedOut.limitUseOfPI) {
                    // limit Use of PI cookie exists so we can't delete all cookies
                    setJsonCookie(WbStatePrivacyCookieKey, { ...updatedOptOutState }, 10950);
                } else {
                    if (userOptedOut.limitUseOfPI === false || updatedOptOutState.doNotSell === false) {
                        setJsonCookie(WbStatePrivacyCookieKey, { ...updatedOptOutState }, 10950);
                    } else {
                        // drop all privacy cookies at this point since none is enabled.
                        deleteCookie(WbStatePrivacyCookieKey);
                    }
                }
            }
            statePrivacyUpdate();
            setStatePrivacyOptOutSuccessOccurred(true);
            setStatePrivacyOptOutApiIsLoading(false);
            setUserOptedOut(updatedOptOutState);
        } catch(error) {
            console.log('Error updating State Privacy', error);
            setStatePrivacyOptOutApiIsLoading(false);
            setStatePrivacyOptOutErrorOccurred(true);
        } finally {
            setTimeout(()=>{
                setStatePrivacyOptOutErrorOccurred(false);
                setStatePrivacyOptOutSuccessOccurred(false);
            }, 5000);
        }
    }, [appInstanceId, userOptedOut?.doNotSell, userOptedOut?.limitUseOfPI, dataPrivacyEnabled]);

    const handleLimitUseOfPILinkClick = useCallback(async () => {
        try {
            const newLimitUseOfPIOptOutValue = !userOptedOut?.limitUseOfPI;
            let updatedOptOutState: StatePrivacyPolicyCookieType  = userOptedOut ? { ...userOptedOut } : {} as StatePrivacyPolicyCookieType;
            setLimitUseOfPIOptOutApiIsLoading(true);
            setLimitUseOfPIOptOutErrorOccurred(false);
            setLimitUseOfPIOptOutSuccessOccurred(false);
            await UserProvider.updateLimitUseOfPI(appInstanceId, newLimitUseOfPIOptOutValue);
            if (newLimitUseOfPIOptOutValue) {
                updatedOptOutState = {
                    ...updatedOptOutState,
                    limitUseOfPI: true
                };
                setJsonCookie(WbStatePrivacyCookieKey, { ...updatedOptOutState }, 10950);
            } else {
                updatedOptOutState = {
                    ...updatedOptOutState,
                    limitUseOfPI: false
                };
                if (userOptedOut.doNotSell) {
                    // limit Use of PI cookie exists so we can't delete all privacy cookies
                    setJsonCookie(WbStatePrivacyCookieKey, { ...updatedOptOutState }, 10950);
                } else {
                    if (userOptedOut.doNotSell === false || updatedOptOutState.limitUseOfPI === false) {
                        setJsonCookie(WbStatePrivacyCookieKey, { ...updatedOptOutState }, 10950);
                    } else {
                        // drop all privacy cookies at this point since none is enabled.
                        deleteCookie(WbStatePrivacyCookieKey);
                    }
                }
            }
            statePrivacyUpdate();
            setLimitUseOfPIOptOutSuccessOccurred(true);
            setLimitUseOfPIOptOutApiIsLoading(false);
            setUserOptedOut(updatedOptOutState);
        } catch(error) {
            console.log('Error updating Limit Use of Personal Information', error);
            setLimitUseOfPIOptOutApiIsLoading(false);
            setLimitUseOfPIOptOutErrorOccurred(true);
        } finally {
            setTimeout(()=>{
                setLimitUseOfPIOptOutErrorOccurred(false);
                setLimitUseOfPIOptOutSuccessOccurred(false);
            }, 5000);
        }
    }, [appInstanceId, userOptedOut?.limitUseOfPI, userOptedOut?.doNotSell]);

    return (
        <Container>
            &copy; {translate('copyrightStatementBeginning')}{year}{translate('copyrightStatementEnd')}&nbsp;
            <StyledLink href={routeHelper.terms} target='_self'>{translate('terms')}</StyledLink> |&nbsp;
            <StyledLink href={routeHelper.privacy} target='_self'>{translate('privacy')}</StyledLink> |&nbsp;
            { dataPrivacyEnabled 
                && <>
                    <StatePrivacyLinkView
                        linkCallback={handleStatePrivacyLinkClick}
                        isStatePrivacyLink={true}
                        userOptedOutOfStatePrivacy={userOptedOut?.doNotSell}
                        optOutApiIsLoading={statePrivacyOptOutApiIsLoading}
                        optOutErrorOccurred={statePrivacyOptOutErrorOccurred}
                        optOutSuccessOccurred={statePrivacyOptOutSuccessOccurred}
                    />
                    &nbsp;|&nbsp;
                </> 
            }
            { sensitiveDataRestrictionEnabled 
                && <>
                    <StatePrivacyLinkView
                        linkCallback={handleLimitUseOfPILinkClick}
                        isLimitUseOfPILink={true}
                        userOptedOutOfLimitUseOfPI={userOptedOut?.limitUseOfPI}
                        optOutApiIsLoading={limitUseOfPIOptOutApiIsLoading}
                        optOutErrorOccurred={limitUseOfPIOptOutErrorOccurred}
                        optOutSuccessOccurred={limitUseOfPIOptOutSuccessOccurred}
                    />
                    &nbsp;|&nbsp;
                </> 
            }
            { dataPrivacyEnabled 
                && <>
                    <UsStatePrivacyPolicyLink />
                    &nbsp;|&nbsp;
                </> 
            }
            <StyledLink href={routeHelper.partners} target='_self' title='Partners'>{translate('partners')}</StyledLink> |&nbsp;
            <StyledLink href={routeHelper.adChoices} target='_self' title='Partners'>{translate('adChoices')}</StyledLink> |&nbsp;
            <StyledLink href={routeHelper.sitemap} target='_self'>{translate('sitemap')}</StyledLink> |&nbsp;
            <StyledLink href={routeHelper.usaSitemap} target='_self'>{translate('usa')}</StyledLink> |&nbsp;
            <StyledLink href={routeHelper.faq} target='_self'>{translate('faq')}</StyledLink>
            &nbsp;|&nbsp;
            <StyledLink href={weatherBugAdsForAdvertisersRoute} target='_blank'>{
                translate('weatherBugAdsForAdvertisers')
            }</StyledLink>
        </Container>
    );
}));

CopyrightFooter.displayName = 'CopyrightFooter';

const Container = styled.div`
    font-size: 16px;
    font-weight: 400;
    line-height: 1.25;
    color: rgba(255,255,255,0.5);
`;
